import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { OrderFlowService } from '../order-flow.service';
import { OrderSummaryResponse } from '../../../model/order-summary-response.model';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/auth/auth.service';

@Component({
  selector: 'app-order-flow-order-summary-order-view',
  templateUrl: './order-flow-order-summary-order-detail.html',
  styleUrls: ['./order-flow-order-summary-order-detail.css'],
  standalone: true,
  imports: [CommonModule],
})
export class OrderFlowOrderSummaryOrderViewComponent implements OnInit {
  groupedOrderDetails: { groupName: string; items: { label: string; value: string }[] }[] = [];
  groupedMeasurements: any[] = [];
  groupedAccessories: any[] = [];
  isLoading = true;
  errorMessage = '';
  attachments: { fileName: string; fileUrl: string }[] = [];
  productName = '';
  modelName = '';
  comments: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private orderFlowService: OrderFlowService,
    private sanitizer: DomSanitizer,
    private authService: AuthService,

  ) {}

  ngOnInit(): void {
    const accessToken = this.route.snapshot.paramMap.get('token');
    if(accessToken!=null)
    this.authService.setAccessToken(accessToken);


    this.fetchOrderSummary();
  }

  private fetchOrderSummary(): void {
    const orderNumber = this.route.snapshot.paramMap.get('orderNumber');
    if (!orderNumber) {
      this.errorMessage = 'Order number is missing in the URL.';
      this.isLoading = false;
      return;
    }

    this.orderFlowService.getOrderSummary(orderNumber).subscribe({
      next: (response) => {
        this.processOrderSummary(response);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching order summary:', error);
        this.errorMessage = 'Failed to fetch order summary. Please try again later.';
        this.isLoading = false;
      },
      complete: () => {
        this.clearAttributeOptionsBrackets();
      },
    });
  }

  private processOrderSummary(response: OrderSummaryResponse): void {
    // Process order details
    const groupedDetailsMap = new Map<string, { label: string; value: string }[]>();
    
    response.data.orderDetails.forEach((detail) => {
      if (!groupedDetailsMap.has(detail.group)) {
        groupedDetailsMap.set(detail.group, []);
      }
      groupedDetailsMap.get(detail.group)?.push({ label: detail.label, value: detail.value });
    });

    groupedDetailsMap.set("Product Details", []);
    this.productName = response.data.productChoice.product;
    this.modelName = response.data.productChoice.model;

    response.data.productChoice.attributeOptions.forEach((detail) => {
      groupedDetailsMap.get("Product Details")?.push({ label: detail.name, value: detail.value });
    });


    this.groupedOrderDetails = Array.from(groupedDetailsMap, ([groupName, items]) => ({
      groupName,
      items,
    }));

    // Process measurements
    this.groupedMeasurements = response.data.measure.measurements;

    // Process accessories
    this.groupedAccessories = response.data.measure.accessories.map((accessory) => ({
      accessory: accessory,
      selectedAttributes: accessory.options,
      selectedImage: accessory.options.find((opt) => opt.imageUrl),
    }));

    this.attachments = response.data.attachments;
    this.comments = response.data.comments || null;
    this.comments = (this.comments && this.comments.match(/.{1,130}/g)?.join('\n')) ?? this.comments;
  }

  clearAttributeOptionsBrackets(): void {
    if (environment && environment.production) {
      if (this.groupedMeasurements.length > 0) {
        this.groupedMeasurements.forEach((attribute: any) => {
          if (attribute.attributeOptionName && attribute.attributeOptionName.includes('(')) {
            const match = attribute.attributeOptionName.match(/\((\d+)\)/);
            if (!match) {
              attribute.attributeOptionName = attribute.attributeOptionName.split('(')[0].trim();
            }
          }
        });
      }

      if (this.groupedAccessories.length > 0) {
        this.groupedAccessories.forEach((option: any) => {
          if (option.name && option.name.includes('(')) {
            const match = option.name.match(/\((\d+)\)/);
            if (!match) {
              option.name = option.name.split('(')[0].trim();
            }
          }
        });
      }

      if (this.groupedOrderDetails.length > 0) {
        this.groupedOrderDetails.forEach((group) => {
          group.items.forEach((item) => {
            if (item.label && item.label.includes('(')) {
              const match = item.label.match(/\((\d+)\)/);
              if (!match) {
                item.label = item.label.split('(')[0].trim();
              }
            }
          });
        });
      }
    }
  }
  
  getSafeImageUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }
}
